/**
 * Admin Command data hooks (superadmin only).
 *
 * useAdminOverview — two-stage progressive load:
 *   1. `admin_client_overview` (fast synchronous execution, Appwrite only)
 *      returns teams + memberships + users → the table renders immediately.
 *   2. Per-team credits stream in through the existing `get_credit_balance`
 *      action — exactly ONE call per team (team-wide balance, resolved from
 *      any member's email), serial and paced to respect the shared n8n
 *      rate limit. Each team's row updates as its data lands; failures
 *      degrade to null.
 *
 * useAdminTeams / useAdminUser / mutations — the admin-prefs-manager function
 * for editing team prefs and user labels/prefs.
 */

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../services/query/queryKeys';
import { adminCache, teamFingerprint } from '../services/adminCache';
import { resolveTeamOwnerEmail } from '../utils/adminCredits';
import {
  adminPrefsService,
  type AdminOverviewData,
  type AdminTeam,
  type AdminUser,
  type WritableTeamPrefKey,
} from '../services/backend/appwrite/adminPrefsService';
import { useAuth } from '../contexts/AuthContext';

const useIsSuperAdmin = () => {
  const { currentUser } = useAuth();
  return currentUser?.labels?.includes('superadmin') || currentUser?.email === 'brainiac@brainiacsbots.com';
};

/** Credits + campaign status counts for one team. */
export interface AdminTeamMetrics {
  credits: number | null;
  campaigns_total: number;
  campaigns_by_status: { active: number; paused: number; archived: number };
  leads_total: number;
}

/**
 * Stage 1 — the fast overview (teams + users + prefs, no N8N calls).
 *
 * Durably cached: the last successful payload is persisted to localStorage
 * and used as TanStack's initialData, so the table renders INSTANTLY on
 * every visit. A background refetch keeps it current (the fetch itself is
 * cheap — ~1-2s Appwrite-only); a successful refetch overwrites the cache.
 * Because teams/users change rarely, this makes repeat visits effectively
 * zero-cost while staying eventually fresh.
 */
export const useAdminOverview = () => {
  const isSuperAdmin = useIsSuperAdmin();
  const { currentUser } = useAuth();

  return useQuery({
    queryKey: queryKeys.admin.overview,
    queryFn: async (): Promise<AdminOverviewData> => {
      // Use the admin-prefs-manager function (Appwrite-only, no n8n)
      // instead of the n8n-proxy's admin_client_overview, which shares
      // the API rate limit with lead/campaign calls.
      const { teams } = await adminPrefsService.listTeams();
      // Collect all unique users from team memberships.
      const userIndex = new Map<string, number>();
      const allUsers: AdminUser[] = [];
      for (const team of teams) {
        for (const member of team.members || []) {
          const userKey = member.user_id || `invite:${member.membership_id}`;
          const candidate: AdminUser = {
            $id: userKey,
            email: member.email,
            name: member.name || member.email,
            labels: member.labels || [],
            prefs: {
              status: member.access_status || (member.status === 'pending' ? 'invited' : 'active'),
              profilePictureUrl: member.profile_picture_url || null,
              job_title: member.job_title || null,
            },
            $createdAt: member.user_created_at || member.invited_at || '',
            $updatedAt: member.user_updated_at || '',
            emailVerification: member.email_verification || false,
            status: member.access_status || (member.status === 'pending' ? 'invited' : 'active'),
            team_id: team.$id,
            membership_id: member.membership_id,
            membership_status: member.status,
            invited_at: member.invited_at,
            is_invitation: member.status === 'pending',
            account_enabled: member.account_enabled,
          };
          const existingIndex = userIndex.get(userKey);
          if (existingIndex === undefined) {
            userIndex.set(userKey, allUsers.length);
            allUsers.push(candidate);
          } else if (allUsers[existingIndex].membership_status === 'pending' && member.status === 'accepted') {
            // A person can have memberships in several teams. Prefer an accepted
            // membership as their primary People-directory record.
            allUsers[existingIndex] = candidate;
          }
        }
      }
      const data: AdminOverviewData = {
        teams,
        users: allUsers,
        totals: { users: allUsers.length, teams: teams.length },
      };
      adminCache.saveOverview(data);
      return data;
    },
    // Hydrate from the durable cache — instantly available, marked stale so
    // the background refetch fires on mount without blocking the UI.
    initialData: () => {
      const cached = adminCache.loadOverview<AdminOverviewData>();
      return cached ? cached.data : undefined;
    },
    initialDataUpdatedAt: () => adminCache.loadOverview<AdminOverviewData>()?.savedAt,
    enabled: isSuperAdmin,
    // Treat the cached copy as fresh for an hour; older caches still render
    // immediately but trigger the background refetch sooner.
    staleTime: 60 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: false,
  });
};

/**
 * Stage 2 — per-team metrics (credits + campaign counts) with a durable
 * per-team cache and change detection.
 *
 * On mount, every team's cached metrics are hydrated INSTANTLY from
 * localStorage (fingerprint-matched, TTL-bound). Only teams whose fingerprint
 * changed (new/renamed team, different owner, team updated) or whose cached
 * entry expired go into the enrichment queue — normally zero or a handful.
 * Enrichment is ONE `get_credit_balance` call per team (credits are team-wide
 * — one member's email resolves the whole team's balance, so per-user calls
 * are pure rate-limit waste). Results stream in progressively and are
 * persisted back to the cache, so subsequent visits do no N8N work at all.
 */
export const useAdminTeamMetrics = (teams: AdminTeam[]) => {
  const [metrics, setMetrics] = useState<Record<string, AdminTeamMetrics>>({});
  const [pendingCount, setPendingCount] = useState(0);
  const [refreshingCount, setRefreshingCount] = useState(0);
  const isSuperAdmin = useIsSuperAdmin();
  // Keyed by each team's fingerprint (id + owner + updatedAt + member count)
  // so membership/pref changes on the SAME team id also re-trigger enrichment.
  const fingerprintsKey = teams.map((t) => teamFingerprint(t)).sort().join('§');
  const startedForKey = useRef<string | null>(null);

  useEffect(() => {
    if (!isSuperAdmin || !fingerprintsKey) return;
    if (startedForKey.current === fingerprintsKey) return; // already processed this set
    startedForKey.current = fingerprintsKey;

    let cancelled = false;

    // ── Pass 1: hydrate every cached metric instantly ──────────────────────
    const hydrated: Record<string, AdminTeamMetrics> = {};
    const stale: AdminTeam[] = [];
    teams.forEach((team) => {
      const fingerprint = teamFingerprint(team);
      const cached = adminCache.loadTeamMetrics<AdminTeamMetrics>(fingerprint);
      if (cached) {
        hydrated[team.$id] = cached.metrics;
        return;
      }
      // Expired but fingerprint-matched: show the last-known values while the
      // credits call re-enriches, instead of blanking the row.
      const lastKnown = adminCache.loadTeamMetricsLastKnown<AdminTeamMetrics>(fingerprint);
      if (lastKnown) hydrated[team.$id] = lastKnown.metrics;
      if (resolveTeamOwnerEmail(team)) {
        // list_teams responses carry no owner_email — derive it from members.
        stale.push(team);
      }
    });
    setMetrics(hydrated);

    if (stale.length === 0) {
      setPendingCount(0);
      setRefreshingCount(0);
      return;
    }

    // ── Pass 2: enrich only what's missing/stale, progressively ────────────
    setPendingCount(stale.length);
    setRefreshingCount(stale.length);

    // SKIP n8n enrichment — get_credit_balance hits the n8n-proxy, which has a
    // shared API rate limit with leads/campaigns calls. Even one call per team
    // (paced 1.5s apart) competes with the leads/campaigns traffic and causes
    // "Brainiacs server is temporarily overloaded" errors on the leads view.
    // Admin credits are cosmetic — not worth breaking the leads pipeline for.
    // The cached data from the previous session is shown if available; new
    // teams without a cache entry show — instead of blocking.
    setPendingCount(0);
    setRefreshingCount(0);

    return () => { cancelled = true; };
  }, [fingerprintsKey, isSuperAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  return { metrics, pendingCount, refreshingCount, loading: pendingCount > 0 };
};

/** Teams list from admin-prefs-manager (fresh member labels). */
export const useAdminTeams = () => {
  const isSuperAdmin = useIsSuperAdmin();

  return useQuery({
    queryKey: queryKeys.admin.teams,
    queryFn: () => adminPrefsService.listTeams(),
    enabled: isSuperAdmin,
    staleTime: 60 * 1000,
    retry: 1,
  });
};

/** Single user (labels + prefs) for the user editor. */
export const useAdminUser = (userId: string | null) => {
  const isSuperAdmin = useIsSuperAdmin();

  return useQuery({
    queryKey: queryKeys.admin.user(userId || ''),
    queryFn: () => adminPrefsService.getUser(userId as string),
    enabled: isSuperAdmin && !!userId,
    staleTime: 30 * 1000,
    retry: 1,
  });
};

/** Mutations invalidate both the teams list and the overview (metrics/prefs).
 *  A team-pref change also drops that team's cached metrics fingerprint will
 *  mismatch on next load (team $updatedAt changes), forcing a re-enrich. */
const useInvalidateAdmin = () => {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: ['admin'] });
  };
};

export const useUpdateTeamPrefs = () => {
  const invalidate = useInvalidateAdmin();
  return useMutation({
    mutationFn: ({ teamId, prefs }: { teamId: string; prefs: Partial<Record<WritableTeamPrefKey, string | boolean | null>> }) =>
      adminPrefsService.updateTeamPrefs(teamId, prefs),
    onSuccess: invalidate,
  });
};

export const useUpdateUserLabels = () => {
  const invalidate = useInvalidateAdmin();
  return useMutation({
    mutationFn: ({ userId, labels }: { userId: string; labels: string[] }) =>
      adminPrefsService.updateUserLabels(userId, labels),
    onSuccess: invalidate,
  });
};

export const useUpdateUserPrefs = () => {
  const invalidate = useInvalidateAdmin();
  return useMutation({
    mutationFn: ({ userId, prefs }: { userId: string; prefs: Record<string, any> }) =>
      adminPrefsService.updateUserPrefs(userId, prefs),
    onSuccess: invalidate,
  });
};

/** Admin-side account settings: name + avatar upload + pref updates. */
export const useUpdateUserProfile = () => {
  const invalidate = useInvalidateAdmin();
  return useMutation({
    mutationFn: (input: Parameters<typeof adminPrefsService.updateUserProfile>[0]) =>
      adminPrefsService.updateUserProfile(input),
    onSuccess: invalidate,
  });
};

export type { AdminUser };
